import {
  AVATAR_GENERATOR_ASSET_ROOT,
  avatarLayerSourceList,
  avatarLayerSourceMap,
} from '../avatar/avatarLayerSources';
import type { AvatarLayerSelection } from '../types/user';

export interface AvatarGeneratorOption {
  value: string;
  label: string;
  src: string;
}

const randomItem = <T,>(items: readonly T[]): T => items[Math.floor(Math.random() * items.length)] ?? items[0];

const toOption = (value: string, label: string, src: string): AvatarGeneratorOption => ({
  value,
  label,
  src,
});

const pad2 = (value: number): string => String(value).padStart(2, '0');

const bodySource = (file: string): string => `${AVATAR_GENERATOR_ASSET_ROOT}/Bodies/32x32/${file}`;
const eyesSource = (file: string): string => `${AVATAR_GENERATOR_ASSET_ROOT}/Eyes/32x32/${file}`;
const hairstyleSource = (file: string): string => `${AVATAR_GENERATOR_ASSET_ROOT}/Hairstyles/32x32/${file}`;
const outfitSource = (file: string): string => `${AVATAR_GENERATOR_ASSET_ROOT}/Outfits/32x32/${file}`;
const accessorySource = (file: string): string => `${AVATAR_GENERATOR_ASSET_ROOT}/Accessories/32x32/${file}`;

const propSource = (value: string): string => {
  const [folder, file] = value.split('/');
  return `${AVATAR_GENERATOR_ASSET_ROOT}/${folder}/32x32/${file}`;
};

const buildSequentialFiles = (prefix: string, count: number): string[] =>
  Array.from({ length: count }, (_, index) => `${prefix}_${pad2(index + 1)}.png`);

const buildVariantFiles = (prefix: string, variantsByStyle: Record<string, number>): string[] =>
  Object.entries(variantsByStyle)
    .sort(([a], [b]) => Number(a) - Number(b))
    .flatMap(([style, variantCount]) =>
      Array.from({ length: variantCount }, (_, index) => `${prefix}_${style}_32x32_${pad2(index + 1)}.png`),
    );

const hairstyleVariantCounts: Record<string, number> = {
  '01': 7,
  '02': 7,
  '03': 7,
  '04': 7,
  '05': 7,
  '06': 7,
  '07': 7,
  '08': 7,
  '09': 7,
  '10': 7,
  '11': 7,
  '12': 7,
  '13': 7,
  '14': 7,
  '15': 7,
  '16': 7,
  '17': 7,
  '18': 7,
  '19': 7,
  '20': 7,
  '21': 7,
  '22': 7,
  '23': 7,
  '24': 7,
  '25': 7,
  '26': 7,
  '27': 6,
  '28': 6,
  '29': 6,
};

const sunnysideHairStyleNames: Record<string, string> = {
  '01': 'Shorthair',
  '02': 'Curlyhair',
  '03': 'Longhair',
  '04': 'Spikeyhair',
  '05': 'Bowlhair',
  '06': 'Mophair',
};

const notableHairStyleNames: Record<string, string> = {
  '11': 'Afro',
  '17': 'Ponytail',
  '27': 'Ponytail Long',
  '29': 'Ponytail Side Sweep',
};

const outfitVariantCounts: Record<string, number> = {
  '01': 10,
  '02': 4,
  '03': 4,
  '04': 3,
  '05': 5,
  '06': 4,
  '07': 4,
  '08': 3,
  '09': 3,
  '10': 5,
  '11': 4,
  '12': 3,
  '13': 4,
  '14': 5,
  '15': 3,
  '16': 3,
  '17': 3,
  '18': 4,
  '19': 4,
  '20': 3,
  '21': 4,
  '22': 4,
  '23': 4,
  '24': 4,
  '25': 5,
  '26': 3,
  '27': 3,
  '28': 4,
  '29': 4,
  '30': 3,
  '31': 5,
  '32': 5,
  '33': 3,
};

const accessoryDefinitions = [
  { id: '01', fileName: 'Ladybug', variants: 4 },
  { id: '02', fileName: 'Bee', variants: 3 },
  { id: '03', fileName: 'Backpack', variants: 10 },
  { id: '04', fileName: 'Snapback', variants: 6 },
  { id: '05', fileName: 'Dino_Snapback', variants: 3 },
  { id: '06', fileName: 'Policeman_Hat', variants: 6 },
  { id: '07', fileName: 'Bataclava', variants: 3 },
  { id: '08', fileName: 'Detective_Hat', variants: 3 },
  { id: '09', fileName: 'Zombie_Brain', variants: 3 },
  { id: '10', fileName: 'Bolt', variants: 3 },
  { id: '11', fileName: 'Beanie', variants: 5 },
  { id: '12', fileName: 'Mustache', variants: 5 },
  { id: '13', fileName: 'Beard', variants: 5 },
  { id: '14', fileName: 'Gloves', variants: 4 },
  { id: '15', fileName: 'Glasses', variants: 6 },
  { id: '16', fileName: 'Monocle', variants: 3 },
  { id: '17', fileName: 'Medical_Mask', variants: 5 },
  { id: '18', fileName: 'Chef', variants: 3 },
  { id: '19', fileName: 'Party_Cone', variants: 4 },
] as const;

const accessoryVariantCounts = accessoryDefinitions.reduce<Record<string, number>>((counts, definition) => {
  counts[definition.id] = definition.variants;
  return counts;
}, {});

const bodyFiles = buildSequentialFiles('Body_32x32', 9);
const eyeFiles = buildSequentialFiles('Eyes_32x32', 7);
const hairstyleFiles = buildVariantFiles('Hairstyle', hairstyleVariantCounts);
const outfitFiles = buildVariantFiles('Outfit', outfitVariantCounts);
const accessoryFiles = accessoryDefinitions.flatMap(({ id, fileName, variants }) =>
  Array.from(
    { length: variants },
    (_, index) => `Accessory_${id}_${fileName}_32x32_${pad2(index + 1)}.png`,
  ),
);

const propFiles = [
  'Books/Book_32x32_01.png',
  'Books/Book_32x32_02.png',
  'Books/Book_32x32_03.png',
  'Books/Book_32x32_04.png',
  'Books/Book_32x32_05.png',
  'Books/Book_32x32_06.png',
  'Smartphones/Smartphone_32x32_1.png',
  'Smartphones/Smartphone_32x32_2.png',
  'Smartphones/Smartphone_32x32_3.png',
  'Smartphones/Smartphone_32x32_4.png',
  'Smartphones/Smartphone_32x32_5.png',
] as const;

export const BODY_OPTIONS = bodyFiles.map((file) => {
  const number = file.match(/Body_32x32_(\d+)/)?.[1] ?? '00';
  return toOption(file, `Body ${number}`, bodySource(file));
});

export const EYE_OPTIONS = eyeFiles.map((file) => {
  const number = file.match(/Eyes_32x32_(\d+)/)?.[1] ?? '00';
  return toOption(file, `Eyes ${number}`, eyesSource(file));
});

export const HAIRSTYLE_OPTIONS = hairstyleFiles.map((file) => {
  const style = file.match(/Hairstyle_(\d+)_/)?.[1] ?? '00';
  const variant = file.match(/_32x32_(\d+)/)?.[1] ?? '00';
  const variantCount = hairstyleVariantCounts[style] ?? 1;
  const sunnysideName = sunnysideHairStyleNames[style];
  const notableName = notableHairStyleNames[style];
  const baseLabel = sunnysideName ? `${sunnysideName} (Sunnyside)` : notableName ?? `Hair ${style}`;
  const label = variantCount > 1 ? `${baseLabel} Variant ${variant}` : baseLabel;
  return toOption(file, label, hairstyleSource(file));
});

export const OUTFIT_OPTIONS = outfitFiles.map((file) => {
  const style = file.match(/Outfit_(\d+)_/)?.[1] ?? '00';
  const variant = file.match(/_32x32_(\d+)/)?.[1] ?? '00';
  const variantCount = outfitVariantCounts[style] ?? 1;
  const label = variantCount > 1 ? `Outfit ${style} Variant ${variant}` : `Outfit ${style}`;
  return toOption(file, label, outfitSource(file));
});

export const ACCESSORY_OPTIONS = [
  toOption('none', 'None', ''),
  ...accessoryFiles.map((file) => {
    const id = file.match(/Accessory_(\d+)_/)?.[1] ?? '00';
    const rawName = file.match(/Accessory_\d+_([^_]+(?:_[^_]+)*)_32x32/)?.[1] ?? 'Accessory';
    const variant = file.match(/_32x32_(\d+)/)?.[1] ?? '00';
    const name = rawName
      .split('_')
      .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
      .join(' ');
    const variantCount = accessoryVariantCounts[id] ?? 1;
    const label = variantCount > 1 ? `${name} Variant ${variant}` : name;
    return toOption(file, label, accessorySource(file));
  }),
];

export const PROP_OPTIONS = [
  toOption('none', 'None', ''),
  ...propFiles.map((value) => {
    const [folder, file] = value.split('/');
    const labelBase = file.replace(/\.png$/i, '').replace(/_32x32_/g, ' ').replace(/_/g, ' ');
    return toOption(value, `${folder.slice(0, -1)} ${labelBase}`, propSource(value));
  }),
];

export const DEFAULT_AVATAR_LAYER_SELECTION: AvatarLayerSelection = {
  body: BODY_OPTIONS[0]?.value ?? 'Body_32x32_01.png',
  eyes: EYE_OPTIONS[0]?.value ?? 'Eyes_32x32_01.png',
  hairstyle: HAIRSTYLE_OPTIONS[0]?.value ?? 'Hairstyle_01_32x32_01.png',
  outfit: OUTFIT_OPTIONS[0]?.value ?? 'Outfit_01_32x32_01.png',
  accessory: null,
  prop: null,
};

const hasValue = (options: AvatarGeneratorOption[], value: string | null): boolean =>
  Boolean(value && options.some((option) => option.value === value));

export const normalizeAvatarLayerSelection = (selection?: AvatarLayerSelection): AvatarLayerSelection => {
  const normalizedBody = selection?.body ?? null;
  const normalizedEyes = selection?.eyes ?? null;
  const normalizedHairstyle = selection?.hairstyle ?? null;
  const normalizedOutfit = selection?.outfit ?? null;

  return {
    body: hasValue(BODY_OPTIONS, normalizedBody) ? normalizedBody ?? DEFAULT_AVATAR_LAYER_SELECTION.body : DEFAULT_AVATAR_LAYER_SELECTION.body,
    eyes: hasValue(EYE_OPTIONS, normalizedEyes) ? normalizedEyes ?? DEFAULT_AVATAR_LAYER_SELECTION.eyes : DEFAULT_AVATAR_LAYER_SELECTION.eyes,
    hairstyle: hasValue(HAIRSTYLE_OPTIONS, normalizedHairstyle)
      ? normalizedHairstyle ?? DEFAULT_AVATAR_LAYER_SELECTION.hairstyle
      : DEFAULT_AVATAR_LAYER_SELECTION.hairstyle,
    outfit: hasValue(OUTFIT_OPTIONS, normalizedOutfit)
      ? normalizedOutfit ?? DEFAULT_AVATAR_LAYER_SELECTION.outfit
      : DEFAULT_AVATAR_LAYER_SELECTION.outfit,
    accessory:
      selection?.accessory && hasValue(ACCESSORY_OPTIONS, selection.accessory)
        ? selection.accessory
        : DEFAULT_AVATAR_LAYER_SELECTION.accessory,
    prop: selection?.prop && hasValue(PROP_OPTIONS, selection.prop) ? selection.prop : DEFAULT_AVATAR_LAYER_SELECTION.prop,
  };
};

export const randomizeAvatarLayerSelection = (): AvatarLayerSelection => {
  const accessory = randomItem(ACCESSORY_OPTIONS).value;
  const prop = randomItem(PROP_OPTIONS).value;

  return {
    body: randomItem(BODY_OPTIONS).value,
    eyes: randomItem(EYE_OPTIONS).value,
    hairstyle: randomItem(HAIRSTYLE_OPTIONS).value,
    outfit: randomItem(OUTFIT_OPTIONS).value,
    accessory: accessory === 'none' ? null : accessory,
    prop: prop === 'none' ? null : prop,
  };
};

export const optionForValue = (
  options: AvatarGeneratorOption[],
  value: string | null,
): AvatarGeneratorOption | undefined => {
  if (!value) {
    return undefined;
  }
  return options.find((option) => option.value === value);
};

export const spriteSheetLayerSources = (selection: AvatarLayerSelection): string[] => {
  return avatarLayerSourceList(selection);
};

export const spriteSheetLayerSourceMap = (selection: AvatarLayerSelection) => avatarLayerSourceMap(selection);
